import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, ResponsiveContainer, LineChart, Line
} from "recharts";
import { BarChart3, Users, Target, Wallet, TrendingUp, Clock, Binoculars, ChevronDown, ChevronRight, Sunrise, Sunset } from "lucide-react";

const COLORS = ["#2d5016", "#4a7c25", "#6ba33a", "#8bc34a", "#aed581", "#c5e1a5", "#dcedc8", "#f1f8e9"];
const ATTENDANCE_COLORS = ["#2d5016", "#dc2626"];

const StatisticsPage = () => {
  const { token } = useAuth();
  const [hoursData, setHoursData] = useState(null);
  const [attendanceData, setAttendanceData] = useState(null);
  const [quotaData, setQuotaData] = useState(null);
  const [financeData, setFinanceData] = useState(null);
  const [reservationsData, setReservationsData] = useState(null);
  const [resYear, setResYear] = useState(null);
  const [expandedPreza, setExpandedPreza] = useState(null);
  const [expandedClan, setExpandedClan] = useState(null);

  useEffect(() => {
    if (!resYear) return;
    const headers = { Authorization: `Bearer ${token}` };
    axios.get(`${API}/statistics/charts/reservations?leto=${resYear}`, { headers })
      .then(r => setReservationsData(r.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resYear]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllData = async () => {
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [hours, attendance, quota, finance, reservations] = await Promise.all([
        axios.get(`${API}/statistics/charts/hours`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/statistics/charts/attendance`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/statistics/charts/quota`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/statistics/charts/finance`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/statistics/charts/reservations`, { headers }).catch(() => ({ data: null }))
      ]);
      setHoursData(hours.data);
      setAttendanceData(attendance.data);
      setQuotaData(quota.data);
      setFinanceData(finance.data);
      setReservationsData(reservations.data);
    } catch (error) {
      console.error("Error fetching statistics:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="statistics-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-primary flex items-center gap-2">
          <BarChart3 className="h-7 w-7" />
          Statistike
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Pregled podatkov in grafov</p>
      </div>

      <Tabs defaultValue="ure" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="ure" data-testid="tab-hours" className="flex items-center gap-1 text-xs sm:text-sm">
            <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">Delovne</span> ure
          </TabsTrigger>
          <TabsTrigger value="prisotnost" data-testid="tab-attendance" className="flex items-center gap-1 text-xs sm:text-sm">
            <Users className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">Prisotnost</span>
            <span className="sm:hidden">Pris.</span>
          </TabsTrigger>
          <TabsTrigger value="odstrel" data-testid="tab-quota" className="flex items-center gap-1 text-xs sm:text-sm">
            <Target className="h-3 w-3 sm:h-4 sm:w-4" />
            Odstrel
          </TabsTrigger>
          <TabsTrigger value="finance" data-testid="tab-finance" className="flex items-center gap-1 text-xs sm:text-sm">
            <Wallet className="h-3 w-3 sm:h-4 sm:w-4" />
            Finance
          </TabsTrigger>
          <TabsTrigger value="preze" data-testid="tab-reservations" className="flex items-center gap-1 text-xs sm:text-sm">
            <Binoculars className="h-3 w-3 sm:h-4 sm:w-4" />
            Preže
          </TabsTrigger>
        </TabsList>

        {/* REZERVACIJE PREŽ */}
        <TabsContent value="preze" className="space-y-4">
          {reservationsData ? (
            <>
              {(reservationsData.leta || []).length > 1 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Leto:</span>
                  <select
                    value={reservationsData.leto}
                    onChange={(e) => setResYear(e.target.value)}
                    className="h-8 rounded-md border bg-background px-2 text-sm"
                    data-testid="res-year-select"
                  >
                    {reservationsData.leta.map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                <Card data-testid="res-stat-total">
                  <CardContent className="pt-4 pb-4 text-center">
                    <div className="text-2xl font-bold text-primary">{reservationsData.skupaj}</div>
                    <div className="text-xs text-muted-foreground">Rezervacij v {reservationsData.leto}</div>
                  </CardContent>
                </Card>
                <Card data-testid="res-stat-svit">
                  <CardContent className="pt-4 pb-4 text-center">
                    <div className="text-2xl font-bold text-amber-600">{reservationsData.svit}</div>
                    <div className="text-xs text-muted-foreground">Svit</div>
                  </CardContent>
                </Card>
                <Card data-testid="res-stat-mrak">
                  <CardContent className="pt-4 pb-4 text-center">
                    <div className="text-2xl font-bold text-indigo-600">{reservationsData.mrak}</div>
                    <div className="text-xs text-muted-foreground">Mrak</div>
                  </CardContent>
                </Card>
                <Card data-testid="res-stat-preze">
                  <CardContent className="pt-4 pb-4 text-center">
                    <div className="text-2xl font-bold text-emerald-700">{reservationsData.st_prez ?? 0}</div>
                    <div className="text-xs text-muted-foreground">Uporabljenih prež</div>
                  </CardContent>
                </Card>
                <Card data-testid="res-stat-lovci">
                  <CardContent className="pt-4 pb-4 text-center">
                    <div className="text-2xl font-bold text-emerald-700">{reservationsData.st_lovcev ?? 0}</div>
                    <div className="text-xs text-muted-foreground">Aktivnih lovcev</div>
                  </CardContent>
                </Card>
              </div>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Rezervacije po mesecih ({reservationsData.leto})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div style={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={reservationsData.po_mesecih}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="mesec" tick={{ fontSize: 11 }} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="svit" name="Svit" stackId="a" fill="#d97706" />
                        <Bar dataKey="mrak" name="Mrak" stackId="a" fill="#4f46e5" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
              <div className="grid md:grid-cols-2 gap-4">
                <Card data-testid="res-top-preze">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Analiza po prežah</CardTitle>
                    <p className="text-xs text-muted-foreground">Klikni prežo za razčlenitev po lovcih</p>
                  </CardHeader>
                  <CardContent className="space-y-1">
                    {(reservationsData.preze_detail || []).length === 0 && <p className="text-sm text-muted-foreground">Ni podatkov.</p>}
                    {(reservationsData.preze_detail || []).map((p, i) => (
                      <div key={p.ime} className="border-b last:border-0">
                        <button
                          type="button"
                          onClick={() => setExpandedPreza(expandedPreza === p.ime ? null : p.ime)}
                          className="w-full flex items-center justify-between text-sm py-1.5 hover:bg-muted/50 rounded px-1 text-left"
                          data-testid={`preza-row-${i}`}
                        >
                          <span className="flex items-center gap-1.5 min-w-0">
                            {expandedPreza === p.ime ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
                            <span className="truncate">{i + 1}. {p.ime}</span>
                          </span>
                          <span className="flex items-center gap-1.5 shrink-0">
                            <span className="text-xs text-muted-foreground">{p.st_lovcev} {p.st_lovcev === 1 ? "lovec" : "lovcev"}</span>
                            <Badge variant="secondary">{p.st}×</Badge>
                          </span>
                        </button>
                        {expandedPreza === p.ime && (
                          <div className="pl-7 pb-2 space-y-1" data-testid={`preza-detail-${i}`}>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1"><Sunrise className="h-3 w-3 text-amber-600" /> Svit: {p.svit}</span>
                              <span className="flex items-center gap-1"><Sunset className="h-3 w-3 text-indigo-600" /> Mrak: {p.mrak}</span>
                            </div>
                            {p.clani.map((c, ci) => (
                              <div key={c.ime} className="flex items-center justify-between text-xs py-0.5">
                                <span className={ci === 0 ? "font-semibold text-primary" : ""}>
                                  {c.ime}{ci === 0 && " ★"}
                                </span>
                                <span className="text-muted-foreground">{c.st}× ({Math.round((c.st / p.st) * 100)}%)</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
                <Card data-testid="res-top-clani">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Analiza po lovcih</CardTitle>
                    <p className="text-xs text-muted-foreground">Klikni lovca za razčlenitev po prežah</p>
                  </CardHeader>
                  <CardContent className="space-y-1">
                    {(reservationsData.clani_detail || []).length === 0 && <p className="text-sm text-muted-foreground">Ni podatkov.</p>}
                    {(reservationsData.clani_detail || []).map((c, i) => (
                      <div key={c.ime} className="border-b last:border-0">
                        <button
                          type="button"
                          onClick={() => setExpandedClan(expandedClan === c.ime ? null : c.ime)}
                          className="w-full flex items-center justify-between text-sm py-1.5 hover:bg-muted/50 rounded px-1 text-left"
                          data-testid={`clan-row-${i}`}
                        >
                          <span className="flex items-center gap-1.5 min-w-0">
                            {expandedClan === c.ime ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
                            <span className="truncate">{i + 1}. {c.ime}</span>
                          </span>
                          <span className="flex items-center gap-1.5 shrink-0">
                            <span className="text-xs text-muted-foreground">{c.st_prez} {c.st_prez === 1 ? "preža" : "prež"}</span>
                            <Badge variant="secondary">{c.st}×</Badge>
                          </span>
                        </button>
                        {expandedClan === c.ime && (
                          <div className="pl-7 pb-2 space-y-1" data-testid={`clan-detail-${i}`}>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1"><Sunrise className="h-3 w-3 text-amber-600" /> Svit: {c.svit}</span>
                              <span className="flex items-center gap-1"><Sunset className="h-3 w-3 text-indigo-600" /> Mrak: {c.mrak}</span>
                            </div>
                            {c.preze.map((p, pi) => (
                              <div key={p.ime} className="flex items-center justify-between text-xs py-0.5">
                                <span className={pi === 0 ? "font-semibold text-primary" : ""}>
                                  {p.ime}{pi === 0 && " ★"}
                                </span>
                                <span className="text-muted-foreground">{p.st}× ({Math.round((p.st / c.st) * 100)}%)</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">Ni podatkov o rezervacijah.</p>
          )}
        </TabsContent>

        {/* DELOVNE URE */}
        <TabsContent value="ure" className="space-y-4">
          {hoursData ? (
            <>
              {/* Realizacija po članih */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Realizacija ur po članih</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="w-full overflow-x-auto">
                    <div style={{ minWidth: Math.max(600, hoursData.clani.length * 40), height: 350 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={hoursData.clani} margin={{ top: 5, right: 20, left: 0, bottom: 80 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                          <XAxis dataKey="ime" angle={-45} textAnchor="end" fontSize={10} interval={0} height={80} />
                          <YAxis fontSize={11} />
                          <Tooltip
                            formatter={(val, name) => [val, name === "opravljene" ? "Opravljene" : "Obvezne"]}
                            contentStyle={{ borderRadius: 8, fontSize: 12 }}
                          />
                          <Legend verticalAlign="top" />
                          <Bar dataKey="obvezne" fill="#c5e1a5" name="Obvezne" radius={[2, 2, 0, 0]} />
                          <Bar dataKey="opravljene" fill="#2d5016" name="Opravljene" radius={[2, 2, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Po tipu dela */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Ure po tipu dela</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {hoursData.po_tipu.length > 0 ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Pie
                            data={hoursData.po_tipu}
                            cx="50%" cy="50%"
                            outerRadius={100}
                            dataKey="ure"
                            nameKey="tip"
                            label={({ tip, ure }) => `${tip}: ${ure}h`}
                            labelLine={true}
                            fontSize={11}
                          >
                            {hoursData.po_tipu.map((entry, i) => (
                              <Cell key={entry.tip || `hour-type-${i}`} fill={COLORS[i % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(val) => [`${val} ur`]} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-center text-muted-foreground py-8">Ni podatkov</p>
                    )}
                  </CardContent>
                </Card>

                {/* Po mesecu */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Ure po mesecih</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {hoursData.po_mesecu.length > 0 ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <LineChart data={hoursData.po_mesecu} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                          <XAxis dataKey="mesec" fontSize={11} />
                          <YAxis fontSize={11} />
                          <Tooltip formatter={(val) => [`${val} ur`]} contentStyle={{ borderRadius: 8 }} />
                          <Line type="monotone" dataKey="ure" stroke="#2d5016" strokeWidth={2} dot={{ r: 4 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-center text-muted-foreground py-8">Ni podatkov</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          ) : (
            <Card><CardContent className="py-8 text-center text-muted-foreground">Ni podatkov o urah</CardContent></Card>
          )}
        </TabsContent>

        {/* PRISOTNOST */}
        <TabsContent value="prisotnost" className="space-y-4">
          {attendanceData ? (
            <>
              {/* Po dogodku */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Udeležba po dogodkih (zadnjih 20)</CardTitle>
                </CardHeader>
                <CardContent>
                  {attendanceData.po_dogodku.length > 0 ? (
                    <div className="w-full overflow-x-auto">
                      <div style={{ minWidth: Math.max(600, attendanceData.po_dogodku.length * 50), height: 320 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={attendanceData.po_dogodku} margin={{ top: 5, right: 20, left: 0, bottom: 60 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                            <XAxis dataKey="naziv" angle={-35} textAnchor="end" fontSize={10} interval={0} height={60} />
                            <YAxis fontSize={11} />
                            <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                            <Legend verticalAlign="top" />
                            <Bar dataKey="pridem" fill="#2d5016" name="Pridem" stackId="a" radius={[0, 0, 0, 0]} />
                            <Bar dataKey="ne_pridem" fill="#dc2626" name="Ne pridem" stackId="a" radius={[2, 2, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">Ni podatkov</p>
                  )}
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Po tipu dogodka */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Udeležba po tipu dogodka</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {attendanceData.po_tipu.length > 0 ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={attendanceData.po_tipu} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                          <XAxis dataKey="tip" fontSize={11} />
                          <YAxis fontSize={11} />
                          <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                          <Legend verticalAlign="top" />
                          <Bar dataKey="pridem" fill="#2d5016" name="Pridem" />
                          <Bar dataKey="ne_pridem" fill="#dc2626" name="Ne pridem" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-center text-muted-foreground py-8">Ni podatkov</p>
                    )}
                  </CardContent>
                </Card>

                {/* Po članu (iz arhiva) */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Udeležba po članih (arhiv)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {attendanceData.po_clanu.length > 0 ? (
                      <div className="max-h-[280px] overflow-y-auto space-y-1">
                        {attendanceData.po_clanu.map((m) => (
                          <div key={m.ime} className="flex items-center justify-between py-1 px-2 rounded text-sm hover:bg-muted/50">
                            <span className="truncate flex-1 mr-2">{m.ime}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">{m.prisoten}/{m.skupaj}</span>
                              <Badge variant={m.udelezba >= 70 ? "default" : m.udelezba >= 40 ? "secondary" : "destructive"} className="text-xs min-w-[45px] justify-center">
                                {m.udelezba}%
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground py-8">Ni podatkov v arhivu</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          ) : (
            <Card><CardContent className="py-8 text-center text-muted-foreground">Ni podatkov o prisotnosti</CardContent></Card>
          )}
        </TabsContent>

        {/* PLAN ODSTRELA */}
        <TabsContent value="odstrel" className="space-y-4">
          {quotaData ? (
            <>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Realizacija plana odstrela po kategorijah</CardTitle>
                </CardHeader>
                <CardContent>
                  {quotaData.kategorije.length > 0 ? (
                    <ResponsiveContainer width="100%" height={320}>
                      <BarChart data={quotaData.kategorije} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                        <XAxis dataKey="kategorija" fontSize={12} />
                        <YAxis fontSize={11} />
                        <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                        <Legend verticalAlign="top" />
                        <Bar dataKey="plan" fill="#c5e1a5" name="Plan" radius={[2, 2, 0, 0]} />
                        <Bar dataKey="odstreljeno" fill="#2d5016" name="Odstreljeno" radius={[2, 2, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">Ni podatkov</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Podrobnosti po podkategorijah</CardTitle>
                </CardHeader>
                <CardContent>
                  {quotaData.podrobnosti.length > 0 ? (
                    <div className="w-full overflow-x-auto">
                      <div style={{ minWidth: Math.max(600, quotaData.podrobnosti.length * 45), height: 350 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={quotaData.podrobnosti} margin={{ top: 5, right: 20, left: 0, bottom: 80 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                            <XAxis dataKey="podkategorija" angle={-45} textAnchor="end" fontSize={9} interval={0} height={80} />
                            <YAxis fontSize={11} />
                            <Tooltip
                              formatter={(val, name) => [val, name === "plan" ? "Plan" : name === "odstreljeno" ? "Odstreljeno" : `${val}%`]}
                              contentStyle={{ borderRadius: 8, fontSize: 12 }}
                            />
                            <Legend verticalAlign="top" />
                            <Bar dataKey="plan" fill="#c5e1a5" name="Plan" radius={[2, 2, 0, 0]} />
                            <Bar dataKey="odstreljeno" fill="#2d5016" name="Odstreljeno" radius={[2, 2, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">Ni podatkov</p>
                  )}
                </CardContent>
              </Card>

              {/* Realizacija % tortni */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Realizacija po kategorijah (%)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {quotaData.kategorije.map((cat) => (
                      <div key={cat.kategorija} className="text-center p-4 rounded-lg border">
                        <p className="text-sm font-medium mb-2">{cat.kategorija}</p>
                        <p className={`text-3xl font-bold ${cat.realizacija >= 80 ? "text-red-600" : cat.realizacija >= 50 ? "text-orange-500" : "text-green-600"}`}>
                          {cat.realizacija}%
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">{cat.odstreljeno}/{cat.plan}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card><CardContent className="py-8 text-center text-muted-foreground">Ni podatkov o odstrelu</CardContent></Card>
          )}
        </TabsContent>

        {/* FINANCE */}
        <TabsContent value="finance" className="space-y-4">
          {financeData ? (
            <>
              {/* Pregled */}
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                        <TrendingUp className="h-5 w-5 text-red-600" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Skupaj dolgovi</p>
                        <p className="text-xl font-bold text-red-600">{financeData.skupaj_dolg.toFixed(2)} EUR</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                        <Wallet className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Skupaj plačano</p>
                        <p className="text-xl font-bold text-green-600">{financeData.skupaj_placano.toFixed(2)} EUR</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Po tipu */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Dolgovi po tipu</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {financeData.po_tipu.length > 0 ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Pie
                            data={financeData.po_tipu}
                            cx="50%" cy="50%"
                            outerRadius={100}
                            dataKey="znesek"
                            nameKey="tip"
                            label={({ tip, znesek }) => `${tip}: ${znesek.toFixed(0)} EUR`}
                            labelLine={true}
                            fontSize={11}
                          >
                            {financeData.po_tipu.map((entry, i) => (
                              <Cell key={entry.tip || `fin-type-${i}`} fill={COLORS[i % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(val) => [`${val.toFixed(2)} EUR`]} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-center text-muted-foreground py-8">Ni podatkov</p>
                    )}
                  </CardContent>
                </Card>

                {/* Po članu */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Dolgovi po članih</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {financeData.po_clanu.length > 0 ? (
                      <div className="max-h-[280px] overflow-y-auto space-y-1">
                        {financeData.po_clanu.map((m) => (
                          <div key={m.ime} className="flex items-center justify-between py-1 px-2 rounded text-sm hover:bg-muted/50">
                            <span className="truncate flex-1 mr-2">{m.ime}</span>
                            <Badge variant="destructive" className="text-xs">
                              {m.dolg.toFixed(2)} EUR
                            </Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground py-8">Ni dolgov</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          ) : (
            <Card><CardContent className="py-8 text-center text-muted-foreground">Ni finančnih podatkov</CardContent></Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StatisticsPage;
